import { useState } from "react";
import { Link } from "react-router";
import { CheckIcon, ShoppingCartIcon } from "lucide-react";
import { useAuth } from "@/context/auth-context";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ProgressDots } from "@/components/ui/progress-dots";
import { useStoreMappings, useStoreStatus } from "@/lib/store-queries";
import { DEFAULT_STORE, STORE_IDS, STORE_META, type StoreId } from "@/lib/stores";
import { focusRing } from "@/lib/format";
import { getCurrentWeekId } from "@/lib/week-utils";
import { cn } from "@/lib/utils";

export function PreferencesPage() {
  const { preferredStore, setPreferredStore } = useAuth();
  const [pendingStore, setPendingStore] = useState<StoreId | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function choose(store: StoreId) {
    if (store === preferredStore || pendingStore) return;
    setError(null);
    setPendingStore(store);
    try {
      await setPreferredStore(store);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar tu preferencia.");
    } finally {
      setPendingStore(null);
    }
  }

  return (
    <div className="w-full mx-auto max-w-3xl px-4 py-6 flex-1 overflow-y-auto">
      <Card>
        <CardHeader>
          <CardTitle>Preferencias</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Supermercado</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Compras arma tu carrito en este supermercado. Cada uno guarda su propia cuenta,
              dirección y productos vinculados: cambiar no borra nada.
            </p>
          </div>
          <div role="radiogroup" aria-label="Supermercado" className="space-y-2">
            {STORE_IDS.map((store) => (
              <StoreOption
                key={store}
                store={store}
                selected={preferredStore === store}
                pending={pendingStore === store}
                disabled={pendingStore !== null}
                onSelect={() => void choose(store)}
              />
            ))}
          </div>
          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}
          <Link
            to={`/week/${getCurrentWeekId()}/shopping`}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-sm text-sm font-medium text-primary hover:underline",
              focusRing,
            )}
          >
            <ShoppingCartIcon className="size-4" />
            Ir a Compras
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}

function StoreOption({
  store,
  selected,
  pending,
  disabled,
  onSelect,
}: {
  store: StoreId;
  selected: boolean;
  pending: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  const meta = STORE_META[store];
  const statusQuery = useStoreStatus(store);
  const mappingsQuery = useStoreMappings(store, true);
  const status = statusQuery.data;
  const linkedCount = mappingsQuery.data?.length ?? 0;

  const details: string[] = [];
  if (statusQuery.isLoading) {
    details.push("Cargando…");
  } else if (status?.connected) {
    details.push(`Conectado${status.email ? ` como ${status.email}` : ""}`);
  } else {
    details.push("Sin conectar");
  }
  if (linkedCount > 0) {
    details.push(`${linkedCount} ingrediente${linkedCount === 1 ? "" : "s"} vinculado${linkedCount === 1 ? "" : "s"}`);
  }

  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      disabled={disabled && !pending}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors",
        selected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
        disabled && !pending && "opacity-60",
        focusRing,
      )}
    >
      <span
        className={cn(
          "grid size-5 shrink-0 place-items-center rounded-full border",
          selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
        )}
        aria-hidden
      >
        {selected && <CheckIcon className="size-3.5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold">{meta.label}</span>
          {store === DEFAULT_STORE && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              Predeterminado
            </span>
          )}
        </span>
        <span className="mt-0.5 block break-words text-xs text-muted-foreground">{details.join(" · ")}</span>
      </span>
      {pending && <ProgressDots aria-label="Guardando" />}
    </button>
  );
}
